import { useEffect, useState, type FormEvent } from "react";
import Field from "@/components/base/Field";
import Honeypot from "@/components/base/Honeypot";
import FormStatus from "@/components/base/FormStatus";
import ActionButton from "@/components/base/ActionButton";
import { useEnquirySubmit } from "@/hooks/useEnquirySubmit";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahppnrp14a1h3mr6q7g";
const HONEYPOT = "website_alt";

export default function RetrofitEligibilityForm() {
  const { status, message, submit, reset } = useEnquirySubmit();
  const [referenceId, setReferenceId] = useState("");

  useEffect(() => {
    if (status === "success") {
      setReferenceId(`KLZ-${Date.now().toString(36).toUpperCase().slice(-6)}`);
    }
  }, [status]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit(event.currentTarget, SUBMIT_ADDR, HONEYPOT);
  };

  if (status === "success") {
    return (
      <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
        <FormStatus status={status} message={message} referenceId={referenceId} />
        <button
          type="button"
          onClick={reset}
          className="mt-6 inline-flex cursor-pointer items-center gap-2 font-label text-sm font-semibold text-primary-700 hover:text-primary-800"
        >
          <i className="ri-refresh-line" /> Submit another enquiry
        </button>
      </div>
    );
  }

  return (
    <form
      id="retrofit-eligibility-form"
      data-readdy-form
      onSubmit={handleSubmit}
      noValidate
      className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
    >
      <Honeypot name={HONEYPOT} />

      <fieldset className="border-0">
        <legend className="mb-5 flex items-center gap-2 font-label text-xs font-bold uppercase tracking-[0.18em] text-primary-700">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-100">
            <i className="ri-user-line text-sm" />
          </span>
          Personal Details
        </legend>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <Field label="Full Name" name="name" required placeholder="Your full name" autoComplete="name" />
          <Field label="Mobile Number" name="mobile" variant="tel" required placeholder="10-digit mobile number" autoComplete="tel" />
          <Field label="Email" name="email" variant="email" required placeholder="you@example.com" autoComplete="email" />
          <Field label="City" name="city" required placeholder="Your city" autoComplete="address-level2" />
        </div>
      </fieldset>

      <fieldset className="mt-10 border-0">
        <legend className="mb-5 flex items-center gap-2 font-label text-xs font-bold uppercase tracking-[0.18em] text-primary-700">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-100">
            <i className="ri-car-line text-sm" />
          </span>
          Vehicle Details
        </legend>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <Field
            label="Vehicle Category"
            name="vehicle_category"
            variant="select"
            required
            options={[
              { value: "2-wheeler", label: "2-Wheeler" },
              { value: "3-wheeler", label: "3-Wheeler" },
              { value: "4-wheeler", label: "4-Wheeler (Passenger)" },
              { value: "commercial", label: "Commercial Vehicle" },
              { value: "specialised", label: "Specialised Vehicle" },
            ]}
          />
          <Field label="Manufacturer" name="manufacturer" required placeholder="e.g. Maruti Suzuki" />
          <Field label="Model" name="model" required placeholder="e.g. Wagon R" />
          <Field label="Manufacturing Year" name="manufacturing_year" variant="number" required min={1990} max={2026} placeholder="e.g. 2018" />
          <Field
            label="Fuel Type"
            name="fuel_type"
            variant="select"
            required
            options={[
              { value: "petrol", label: "Petrol" },
              { value: "diesel", label: "Diesel" },
              { value: "cng", label: "CNG" },
              { value: "lpg", label: "LPG" },
              { value: "other", label: "Other" },
            ]}
          />
          <Field
            label="Current Vehicle Condition"
            name="vehicle_condition"
            variant="select"
            required
            options={[
              { value: "excellent", label: "Excellent" },
              { value: "good", label: "Good" },
              { value: "fair", label: "Fair" },
              { value: "needs_inspection", label: "Needs inspection" },
            ]}
          />
        </div>
      </fieldset>

      <fieldset className="mt-10 border-0">
        <legend className="mb-5 flex items-center gap-2 font-label text-xs font-bold uppercase tracking-[0.18em] text-primary-700">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-100">
            <i className="ri-route-line text-sm" />
          </span>
          Usage Details
        </legend>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <Field
            label="Average Daily Distance"
            name="average_daily_distance"
            variant="select"
            required
            options={[
              { value: "0-20", label: "Up to 20 km" },
              { value: "20-50", label: "20 – 50 km" },
              { value: "50-100", label: "50 – 100 km" },
              { value: "100-200", label: "100 – 200 km" },
              { value: "200-plus", label: "200 km and above" },
            ]}
          />
          <Field
            label="Expected Range"
            name="expected_range"
            variant="select"
            options={[
              { value: "not-sure", label: "Not sure yet" },
              { value: "up-to-60", label: "Up to 60 km" },
              { value: "60-100", label: "60 – 100 km" },
              { value: "100-150", label: "100 – 150 km" },
              { value: "150-plus", label: "150 km and above" },
            ]}
            hint="Range is configuration dependent and confirmed after assessment."
          />
          <Field label="Payload / Passenger Requirement" name="payload_requirement" placeholder="e.g. 4 passengers or 500 kg" />
          <Field
            label="Primary Application"
            name="primary_application"
            variant="select"
            required
            options={[
              { value: "personal", label: "Personal commuting" },
              { value: "passenger", label: "Passenger transport" },
              { value: "cargo", label: "Cargo / delivery" },
              { value: "institutional", label: "Institutional use" },
              { value: "other", label: "Other" },
            ]}
          />
          <Field
            label="Commercial / Personal"
            name="usage_type"
            variant="select"
            required
            options={[
              { value: "personal", label: "Personal" },
              { value: "commercial", label: "Commercial" },
            ]}
          />
        </div>
      </fieldset>

      <fieldset className="mt-10 border-0">
        <legend className="mb-5 flex items-center gap-2 font-label text-xs font-bold uppercase tracking-[0.18em] text-primary-700">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-100">
            <i className="ri-image-add-line text-sm" />
          </span>
          Vehicle Photos &amp; Documents
        </legend>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <Field label="Vehicle Front Photo" name="vehicle_front_photo" variant="file" accept="image/*" hint="Optional (JPG / PNG)" />
          <Field label="Vehicle Side Photo" name="vehicle_side_photo" variant="file" accept="image/*" hint="Optional (JPG / PNG)" />
          <Field label="RC / Technical Document" name="vehicle_rc_document" variant="file" accept="image/*,.pdf" hint="Optional, where legally appropriate" />
        </div>
      </fieldset>

      <div className="mt-10 flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
        <input
          id="retrofit-consent"
          type="checkbox"
          name="consent"
          value="yes"
          required
          className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-primary-600"
        />
        <label htmlFor="retrofit-consent" className="text-xs leading-relaxed text-foreground-700">
          I consent to being contacted about this enquiry and understand that final retrofit
          feasibility is subject to technical assessment.
        </label>
      </div>

      <div className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
        <ActionButton type="submit" variant="primary" size="lg" icon="ri-flashlight-line" disabled={status === "submitting"}>
          {status === "submitting" ? "Submitting…" : "Check Retrofit Eligibility"}
        </ActionButton>
        <p className="text-[11px] leading-relaxed text-foreground-500">
          No eligibility, cost or range is promised before assessment.
        </p>
      </div>

      <FormStatus status={status} message={message} />
    </form>
  );
}